import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutGrid, ClipboardList, BarChart3, Users, Menu, X } from 'lucide-react';

// Mobile Bottom Navigation for Admin
export const MobileNavAdmin = ({ isOpen, setIsOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const navItems = [
    { path: '/dashboard', icon: LayoutGrid, label: 'Dashboard' },
    { path: '/surveys', icon: ClipboardList, label: 'Surveys' },
    { path: '/analytics', icon: BarChart3, label: 'Analytics' },
    { path: '/users', icon: Users, label: 'Users' },
  ];
  
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  
  return (
    <>
      {/* Bottom Navigation Bar */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-xl border-t border-slate-800 z-50 safe-area-bottom">
        <div className="flex items-center justify-around px-2 py-2">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all ${
                isActive(item.path) 
                  ? 'text-cyan-400' 
                  : 'text-slate-500'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{item.label}</span>
              {/* Active Dot */}
              <div className={`w-1 h-1 rounded-full transition-all ${
                isActive(item.path) ? 'bg-cyan-400' : 'bg-transparent'
              }`} />
            </button>
          ))}
        </div>
      </nav>
      
      {/* Navigation Dots Indicator */}
      <div className="lg:hidden fixed bottom-20 left-1/2 transform -translate-x-1/2 flex items-center gap-1.5 bg-slate-800/80 backdrop-blur-sm px-3 py-1.5 rounded-full z-40">
        {navItems.map((item, index) => (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className={`w-2 h-2 rounded-full transition-all ${
              isActive(item.path) 
                ? 'bg-cyan-400 w-4' 
                : 'bg-slate-600 hover:bg-slate-500'
            }`}
            aria-label={item.label}
          />
        ))}
      </div>
    </>
  );
};

// Mobile Bottom Navigation for Doctor/Sponsor
export const MobileNavDoctor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const navItems = [
    { path: '/doctor', icon: LayoutGrid, label: 'Dashboard' },
    { path: '/surveys', icon: ClipboardList, label: 'Surveys', checkPrefix: true },
  ];
  
  const isActive = (path, checkPrefix) => {
    if (checkPrefix) {
      return location.pathname.includes('/surveys/') && location.pathname.includes('/respond');
    }
    return location.pathname === path;
  };
  
  return (
    <>
      {/* Bottom Navigation Bar */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-xl border-t border-slate-800 z-50 safe-area-bottom">
        <div className="flex items-center justify-around px-4 py-2">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center gap-1 px-6 py-2 rounded-xl transition-all ${
                isActive(item.path, item.checkPrefix) 
                  ? 'text-cyan-400' 
                  : 'text-slate-500'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{item.label}</span>
              {/* Active Dot */}
              <div className={`w-1 h-1 rounded-full transition-all ${
                isActive(item.path, item.checkPrefix) ? 'bg-cyan-400' : 'bg-transparent'
              }`} />
            </button>
          ))}
        </div>
      </nav>
      
      {/* Navigation Dots Indicator */}
      <div className="lg:hidden fixed bottom-20 left-1/2 transform -translate-x-1/2 flex items-center gap-2 bg-slate-800/80 backdrop-blur-sm px-3 py-1.5 rounded-full z-40">
        {navItems.map((item) => (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className={`w-2 h-2 rounded-full transition-all ${
              isActive(item.path, item.checkPrefix) 
                ? 'bg-cyan-400 w-4' 
                : 'bg-slate-600 hover:bg-slate-500'
            }`}
            aria-label={item.label}
          />
        ))}
      </div>
    </>
  );
};

// Mobile Header Component
export const MobileHeader = ({ user, onLogout, title, showBack, onBack }) => {
  const navigate = useNavigate();
  
  return (
    <header className="lg:hidden sticky top-0 bg-slate-900/95 backdrop-blur-xl border-b border-slate-800 z-30 safe-area-top">
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          {showBack ? (
            <button onClick={onBack || (() => navigate(-1))} className="p-2 -ml-2 text-slate-400">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          ) : (
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
          )}
          <span className="text-lg font-bold text-white">
            {title || <>KAP<span className="text-cyan-400">Surv</span></>}
          </span>
        </div>
        <button 
          onClick={() => navigate('/auth')}
          className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center text-white text-sm font-semibold"
        >
          {user?.name?.charAt(0) || 'U'}
        </button>
      </div>
    </header>
  );
};

export default MobileNavAdmin;
